<!-- 임플 단계에서의 레이아웃 디폴트 -->

<template>
  <div>
    <IGADefault>
      <slot />
    </IGADefault>
  </div>
</template>

<script setup lang="ts">
import { library } from '@fortawesome/fontawesome-svg-core';
import { faEnvelope, faInbox } from '@fortawesome/pro-regular-svg-icons';
import { faCalendarRange, faEarthAfrica, faEarthAmerica, faEarthAsia, faEarthEurope, faFlag, faGift, faImage, faMemoCircleInfo, faPencil, faPlane, faTicket, faTimeline, faUsers } from '@fortawesome/pro-solid-svg-icons';
import { filter } from 'lodash-es';
import { useDYAdminMainStore } from '~/modules/nuxt3-module-admin-doyac/stores/main';
import { useNationInfoApi, type NationInfo } from '~/modules/nuxt3-module-myfirstguide/composables/nation-info';
import IGADefault from '~~/modules/nuxt3-module-admin-ingang/layout/IGADefault.vue';
import { faBook, faPresentationScreen } from "@fortawesome/pro-regular-svg-icons";
import { useDYAuth } from "~/modules/nuxt3-module-doyac/composables/auth";
import dayjs from 'dayjs';
const userStore = $dy__useDYUserStore()

library.add(faMemoCircleInfo, faTimeline, faEarthEurope, faEarthAsia, faEarthAmerica, faEarthAfrica, faPlane, faPencil, faEnvelope, faInbox, faGift, faCalendarRange, faTicket, faImage, faUsers, faFlag)

const adminMainStore = useDYAdminMainStore()
const { getList } = useNationInfoApi()

const nations = ref<NationInfo[]>()

const makingChildren = (continent: string) => {
  const filtered = filter(nations.value, { continent })

  let icon = faEarthEurope
  switch (continent) {
    case '아시아': icon = faEarthAsia; break;
    case '유럽': icon = faEarthEurope; break;
    case '북아메리카': icon = faEarthAmerica; break;
    case '아프리카': icon = faEarthAfrica; break;
  }

  return filtered.map((nation) => {
    const cityData = (nation.cities ?? []).map((city) => {
      return {
        id: `city-${city.no}-${city.cityNameEn}`,
        title: city.cityFrameName,
        icon: icon,
        to: `/tourguide/${city.no}`
      }
    })

    return {
      id: `nation-${nation.nationNameEn}`,
      title: nation.nationName,
      icon: icon,
      children: cityData
    }
  })
}
onMounted(async () => {
  await userStore.ready()
  // 임플 단계에서의 메뉴 추가는 윗 단계의 메뉴가 모두 만들어진 이후에 해야하기 때문에 대기없이 바로 하면
  // 적용되지 않는다. 아래처럼 반드시 타임아웃을 걸어서 적용할 것!
  const { isEngineer, isTeamLeader, isCEO } = useDYAuth()
  setTimeout(async () => {
    const result = await getList({ open: 'all', includes: ['cityAll'] })
    nations.value = result.items

    adminMainStore.addMenu(
      'manual-pay-list',
      { id: 'tour-payment-list', title: '투어', to: '/payments/tour-guides?_startDay=' + dayjs().subtract(3, 'year').format('YYYY-MM-DD'), icon: faPlane },
      'prepend'
    )
    adminMainStore.addMenu(
      'tour-payment-list',
      {
        id: 'gift-order-list',
        title: '선물하기 주문내역',
        to: '/payments/gift-orders',
        icon: faGift
      },
      'append'
    )
    adminMainStore.addMenu(
      'gift-order-list',
      {
        id: 'coupang-payment-list',
        title: '쿠팡 결제내역',
        to: '/payments/coupang',
        icon: faPlane
      },
      'append'
    )
    // 2026-06-10 추가 — 쿠팡 결제내역 바로 다음 형제로 '티켓 결제내역' (ticket-proxy 입장권 대리예약 결제, tg_ticket_book)
    adminMainStore.addMenu(
      'coupang-payment-list',
      {
        id: 'ticket-payment-list',
        title: '티켓 결제내역',
        to: '/payments/tickets',
        icon: faTicket
      },
      'append'
    )

    adminMainStore.addMenu('contents-management-header', {
      id: 'constents-management-group',
      title: '투어관리',
      icon: faEarthEurope,
      children: [
        {
          id: 'asia-group',
          title: '아시아',
          icon: faEarthAsia,
          children: makingChildren('아시아')
        },
        {
          id: 'europe-group',
          title: '유럽',
          icon: faEarthEurope,
          children: makingChildren('유럽')
        },
        {
          id: 'north-america-group',
          title: '북아메리카',
          icon: faEarthAmerica,
          children: makingChildren('북아메리카')
        },
        {
          id: 'africa-group',
          title: '아프리카',
          icon: faEarthAfrica,
          children: makingChildren('아프리카')
        },
      ],
    })

    // 260604 '경로 아이콘 등록' → '참고경로 아이콘 등록' 으로 이름 변경 + 위치 이동.
    //   기존: 투어관리 그룹 마지막 자식('africa-group' append).
    //   변경: 콘텐츠 관리 > '디자인 틀 관리'(design-frame-manage)의 형제로 그 바로 아래 배치.
    //   design-frame-manage 는 공유 IGADefault.vue(콘텐츠 관리 헤더 하위, 관리자 메뉴)에서 추가되며,
    //   addMenu append = 매칭 id 바로 뒤에 형제로 삽입한다(자식 아님).
    //   참고: 디자인 틀 관리는 관리자(비파트너) 메뉴에만 존재 → 파트너 계정엔 콘텐츠 관리 섹션이 없어 이 메뉴도 노출되지 않는다(의도된 동작).
    //   여전히 공유 강의 아이콘 관리 페이지(/course/lectureIcon)를 재사용한다(투어 참고경로 마커 아이콘 풀).
    adminMainStore.addMenu('design-frame-manage', {
      id: 'tour-route-icon-manage',
      title: '참고경로 아이콘 등록',
      icon: faImage,
      to: '/course/lectureIcon'
    }, 'append')

    adminMainStore.addMenu('contents-management-header', {
      id: 'constents-nation-group',
      title: '국가관리',
      icon: faEarthEurope,
      children: [
        {
          id: 'asia-group',
          title: '국가정보 등록',
          icon: faEarthAsia,
          to: '/nations'
        },
        {
          id: 'city-info-list',
          title: '도시별 특별사항',
          icon: faCalendarRange,
          to: '/cities/info'
        },
      ],
    })

    // adminMainStore.addMenu('personal-management-header', { id: 'info-url-header', title: '정보 URL', isHeader: true }, 'prepend')
    adminMainStore.addMenu('log-viewer-manage', {
      id: 'email-management-group',
      title: '이메일',
      icon: faEnvelope,
      children: [
        {
          id: 'email-inbox',
          title: '메일함',
          icon: faInbox,
          to: '/email/inbox'
        },
        {
          id: 'email-account-list',
          title: '계정 등록관리',
          icon: faEnvelope,
          to: '/emailAccount'
        },
        {
          id: 'email-send-history',
          title: '메일 발송 내역',
          icon: faEnvelope,
          to: '/mail'
        },
        {
          id: 'email-send-write',
          title: '메일 발송',
          icon: faEnvelope,
          to: '/mail/write'
        },
      ]
    })

    // 계정 공유(ACCOUNT-SHARE.md): 개발자관리 > 이메일 바로 아래에 전체 공유 목록
    adminMainStore.addMenu('email-management-group', {
      id: 'tour-share-list',
      title: '투어 공유 목록',
      icon: faUsers,
      to: '/tour-shares'
    }, 'append')

    // 260724 위치 이동 — '서드파티 상품 관리'.
    //   기존: 콘텐츠 관리 > 투어관리 그룹 바로 다음 형제('constents-management-group' append).
    //   변경: 개발자 관리 그룹 > '투어 공유 목록' 바로 다음 형제(그룹의 마지막)로 이동.
    //   개발자 관리 그룹(group-site)은 공유 IGADefault.vue 에서 isEngineer 일 때만 생성되므로,
    //   비-엔지니어 계정에서는 앵커(tour-share-list)가 없어 addMenu 가 조용히 무시된다 → 자동으로 미노출.
    //   ⚠ 그래서 이 호출은 반드시 위 'tour-share-list' 추가 '뒤'에 있어야 한다(앵커가 먼저 존재해야 함).
    adminMainStore.addMenu('tour-share-list', {
      id: 'third-party-product-list',
      title: '서드파티 상품 관리',
      icon: faPencil,
      to: '/tourguide/thirdParty'
    }, 'append')

    // ⚠ 위 이동에 따라 앵커를 'third-party-product-list' → 'constents-management-group' 으로 교체했다.
    //   (그대로 뒀으면 정보 URL·티켓구매 관리 그룹까지 개발자 관리로 통째로 딸려간다)
    //   'constents-management-group'(투어관리) append 라 콘텐츠 관리 내 순서는 서드파티만 빠지고 그대로 유지된다.
    adminMainStore.addMenu('constents-management-group', {
      id: 'info-url-group',
      title: '정보 URL',
      icon: faMemoCircleInfo,
      children: [
        {
          id: 'info-url-list',
          title: '정보 URL 목록',
          icon: faMemoCircleInfo,
          to: isEngineer.value || isCEO.value ? '/info-urls' : '/info-urls?userIdForNotice=' + userStore.id
        },
        {
          id: 'change-info-list',
          title: '변경내역',
          icon: faTimeline,
          to: isEngineer.value || isCEO.value ? '/change-infos' : '/change-infos?infoUrl:userIdForNotice=' + userStore.id
        },
      ]
    }, "append")

    // 2026-05-19 추가 — 콘텐츠관리 > 정보 URL 그룹 바로 다음에 별도 티켓 상품 그룹 (ticket-proxy)
    // 2026-08-10 (사용자 지시) 메뉴명 '티켓구매 관리' → '티켓 상품 관리'.
    //   이 화면에서 하는 일은 구매(주문) 관리가 아니라 크롤러가 채운 **상품** 관리다.
    //   ⚠ 메뉴 id(ticket-product-group)는 다른 addMenu 의 앵커가 될 수 있으므로 바꾸지 않는다.
    adminMainStore.addMenu('info-url-group', {
      id: 'ticket-product-group',
      title: '티켓 상품 관리',
      icon: faTicket,
      children: [
        {
          // 2026-08-10 frame/contents 분리 — 진입점이 '고객 노출 상품(프레임)' 목록이 됐다.
          //   여기서 상품 하나를 열면 그 안에 공급원별 상품(공식/트립닷컴)이 들어 있다.
          //   ⚠ 메뉴 id 는 다른 addMenu 의 앵커가 될 수 있으므로 바꾸지 않는다.
          id: 'ticket-product-list',
          title: '티켓 상품 목록',
          icon: faTicket,
          to: '/ticket-product-frames'
        },
      ]
    }, "append")

    // 260721 추가 — '투어관리' 그룹 바로 다음 형제로 '루트 신고 목록'(tg_tourguide_route_report).
    //   사용자가 투어 플레이어에서 보낸 제보 목록. 엔지니어/대표가 아니면 본인 담당 투어의 신고만 걸러 진입한다
    //   (변경내역 메뉴가 담당자별로 걸러 들어가는 것과 동일한 방식).
    //   ⚠ 이 호출은 반드시 이 블록의 '마지막'이어야 한다. addMenu(id,'append') 는 매칭된 항목 '바로 뒤'에 끼워넣는데,
    //     뒤에 오는 다른 addMenu 가 같은 앵커(constents-management-group)를 다시 잡으면
    //     그것들이 사이에 끼어들어 이 메뉴가 아래로 밀린다(초기 구현에서 실제로 티켓구매 관리 아래까지 밀렸음).
    adminMainStore.addMenu('constents-management-group', {
      id: 'route-report-list',
      title: '루트 신고 목록',
      icon: faFlag,
      to: isEngineer.value || isCEO.value ? '/route-reports' : '/route-reports?contents:managerId=' + userStore.id
    }, 'append')
  }, 500)
})

</script>

<style></style>
